import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AuthContext } from "../context/AuthContext";

import {
  FaTachometerAlt,
  FaNewspaper,
  FaMapMarkerAlt,
  FaUsers,
  FaCalendarAlt,
  FaListAlt,
  FaUserShield,
  FaClipboardCheck,
  FaQuestionCircle,
  FaCog,
  FaBuilding,
  FaUser,
  FaClock,
  FaChartLine,
  FaTasks,
  FaShieldAlt,
} from "react-icons/fa";
import { GrIntegration } from "react-icons/gr";
 
// All tabs
const allTabs = [
  "Dashboard",
  "News Feed",
  "Locations",
  "People",
  "Schedule",
  "Open Schedules",
  "Timesheets",
  "Analytics",
  "Tasks",
  "Super Admin",
  "Compliance",
  "Integrations",
];
 
// Map Routes → Names
const routeToTabMap = {
  "/dashboard": "Dashboard",
  "/newsfeed": "News Feed",
  "/locations": "Locations",
  "/people": "People",
  "/schedule": "Schedule",
  "/openschedules": "Open Schedules",
  "/timesheets": "Timesheets",
  "/analytics": "Analytics",
  "/tasks": "Tasks",
  "/superadmin": "Super Admin",
  "/compliance": "Compliance",
  "/integrations": "Integrations",
};
 
// Icons
const tabIcons = {
  Dashboard: <FaTachometerAlt className="text-xl" />,
  "News Feed": <FaNewspaper className="text-xl" />,
  Locations: <FaMapMarkerAlt className="text-xl" />,
  People: <FaUsers className="text-xl" />,
  Schedule: <FaCalendarAlt className="text-xl" />,
  "Open Schedules": <FaListAlt className="text-xl" />,
  Timesheets: <FaClock className="text-xl" />,
  Analytics: <FaChartLine className="text-xl" />,
  Tasks: <FaTasks className="text-xl" />,
  "Super Admin": <FaUserShield className="text-xl" />,
  Compliance: <FaShieldAlt className="text-xl" />,
  Integrations: <GrIntegration className="text-xl" />,
};
 
export default function NavTabs({
  isMobile = false,
  onLinkClick,
  onSidebarStateChange, // NEW → tells parent expanded/collapsed state
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname.toLowerCase();
  const { user } = useContext(AuthContext);

  
 
  const getTab = () => {
    const matched = Object.keys(routeToTabMap).find((r) =>
      currentPath.startsWith(r)
    );
    return matched ? routeToTabMap[matched] : "Dashboard";
  };
 
  const [activeTab, setActiveTab] = useState(getTab());
 
  useEffect(() => {
    setActiveTab(getTab());
  }, [currentPath]);
 
  // Sidebar expanded/collapsed
  const [isExpanded, setIsExpanded] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);

 
  // Notify parent whenever sidebar state changes
  useEffect(() => {
    if (typeof onSidebarStateChange === "function") {
      onSidebarStateChange(isExpanded);
    }
  }, [isExpanded]);
 
  // Filter based on user role
 const filteredTabs = allTabs.filter((tab) => {
  if (!user) return false;
  if (user.role === "superadmin") return tab === "Super Admin";
  
  // Admin and Manager see all admin features
  if (user.role === "admin" || user.role === "manager") {
    return tab !== "Super Admin";
  }
  
  // Employees/careWorkers see limited tabs
  return [
    "Dashboard",
    "News Feed",
    "Schedule",
    "Open Schedules",
    "Timesheets"
  ].includes(tab);
});


  const isAdmin=user?.role==="admin";
 
  return (
    <motion.div
      initial={isMobile ? { x: -280 } : { opacity: 1 }}
      animate={isMobile ? { x: 0 } : { opacity: 1 }}
      exit={isMobile ? { x: -280 } : { opacity: 1 }}
      className={`
        ${isMobile
          ? "fixed inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-white via-blue-50/30 to-blue-100/20 backdrop-blur-md shadow-2xl md:hidden border-r border-gray-200/50"
          : `
            hidden md:flex bg-gradient-to-b from-gray-800 via-gray-900 to-gray-800 text-white h-screen py-4
            transition-all duration-300
            ${isExpanded ? "w-64" : "w-20"}
          `
        }
        flex flex-col justify-between
      `}
      onMouseEnter={() => !isMobile && setIsExpanded(true)}
      onMouseLeave={() => !isMobile && setIsExpanded(false)}
    >
      {/* TOP MENU LIST */}
      <div className="flex flex-col px-3 space-y-1">
        {filteredTabs.map((tab, index) => (
          <motion.button
            key={tab}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
            whileHover={{ scale: 1.02, x: 4 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              setActiveTab(tab);
              navigate(`/${tab.toLowerCase().replace(/ /g, "")}`);
              onLinkClick && onLinkClick();
            }}
            className={`
              flex items-center gap-3 w-full py-3 px-3 rounded-xl transition-all font-medium
              ${activeTab === tab
                ? isMobile 
                  ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg"
                  : "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg"
                : isMobile
                  ? "text-black hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100"
                  : "text-gray-300 hover:bg-gray-700"
              }
            `}
          >
            {/* Icon */}
            <div className="flex justify-center w-8">{tabIcons[tab]}</div>
 
            {/* Label (Only visible when expanded) */}
            <span
              className={`whitespace-nowrap transition-opacity duration-200 ${
                (isMobile || isExpanded) ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
            >
              {tab}
            </span>
          </motion.button>
        ))}
      </div>
 
      {/* BOTTOM MENU */}
      <motion.div className="flex flex-col px-3 pt-4 pb-4 space-y-2 border-t border-gray-700/50">
        <motion.button
          whileHover={{ scale: 1.02, x: 4 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate("/contact")}
          className={`flex items-center w-full gap-3 px-3 py-3 transition rounded-xl ${
            isMobile ? "text-black hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100" : "text-gray-300 hover:bg-gray-700"
          }`}
        >
          <div className="flex justify-center w-8">
            <FaQuestionCircle className="text-xl" />
          </div>
 
          <span
            className={`transition-opacity duration-200 ${
              (isMobile || isExpanded) ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          >
            Help & Support
          </span>
        </motion.button>
 
{/* SETTINGS WRAPPER (relative for flyout) */}
<motion.div className="relative">
  <motion.button
    whileHover={{ scale: 1.02, x: 4 }}
    whileTap={{ scale: 0.98 }}
    onClick={() => setOpenSettings(prev => !prev)}
    className={`flex items-center w-full gap-3 px-3 py-3 transition rounded-xl ${
      isMobile ? "text-black hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100" : "text-gray-300 hover:bg-gray-700"
    }`}
  >
    <div className="flex justify-center w-8">
      <FaCog className="text-xl" />
    </div>

    <span
      className={`transition-opacity duration-200 ${
        (isMobile || isExpanded) ? "opacity-100" : "opacity-0 pointer-events-none"
      }`} 
    >
      Settings
    </span>

    {(isMobile || isExpanded) && (
      <motion.span
        animate={{ rotate: openSettings ? 180 : 0 }}
        className="text-gray-400 ml-auto text-xs"
      >
        ▼
      </motion.span>
    )}
  </motion.button>

  {/* DROPDOWN MENU - appears below button */}
  <AnimatePresence>
    {openSettings && (
      <motion.div
        initial={{ opacity: 0, y: -10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -10, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className={`
          absolute top-full left-0 mt-2 w-full
          ${isMobile 
            ? "bg-white/98 backdrop-blur-md border border-gray-300/50 shadow-xl" 
            : "bg-gray-800/98 backdrop-blur-md border border-gray-700/50 shadow-2xl"
          }
          rounded-xl
          p-2
          flex flex-col space-y-1.5 z-[10000]
        `}
      >
        <motion.button
          whileHover={{ scale: 1.02, x: 2 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => {
            navigate('/profile');
            setOpenSettings(false);
          }}
          className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all font-medium text-sm ${
            isMobile 
              ? "text-black hover:bg-gradient-to-r hover:from-blue-600 hover:to-blue-700 hover:text-white border border-gray-200" 
              : "text-gray-300 hover:bg-blue-600 hover:text-white border border-gray-600 hover:border-blue-500"
          }`}
        >
          <FaUser className="text-base" />
          <span>Profile Settings</span>
        </motion.button>

        {isAdmin && (
          <motion.button
            whileHover={{ scale: 1.02, x: 2 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              navigate('/businessprofile');
              setOpenSettings(false);
            }}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all font-medium text-sm ${
              isMobile 
                ? "text-black hover:bg-gradient-to-r hover:from-blue-600 hover:to-blue-700 hover:text-white border border-gray-200" 
                : "text-gray-300 hover:bg-blue-600 hover:text-white border border-gray-600 hover:border-blue-500"
            }`}
          >
            <FaBuilding className="text-base" />
            <span>Business Profile</span>
          </motion.button>
        )}
      </motion.div>
    )}
  </AnimatePresence>

</motion.div>


      </motion.div>
    </motion.div>
  );
}
 




// import React, { useState, useEffect, useContext } from "react";
// import { useNavigate, useLocation } from "react-router-dom";
// import { AuthContext } from '../context/AuthContext'; // Import your AuthContext

// // Define all possible tabs
// const allTabs = ["Me", "News Feed", "Locations", "People", "Schedule", "Open Schedules", "Reports", "Super Admin"];

// // Map routes to tab names for precise matching
// const routeToTabMap = {
//     "/me": "Me",
//     "/newsfeed": "News Feed",
//     "/locations": "Locations",
//     "/people": "People",
//     "/schedule": "Schedule",
//     "/openschedules": "Open Schedules",
//     "/reports": "Reports",
//     "/superadmin": "Super Admin" // Ensure this route matches your SuperAdminPage
// };

// // Add prop `isMobile` to control rendering for mobile view
// // Add prop `onLinkClick` to handle closing the mobile menu after a link is clicked
// export default function NavTabs({ isMobile, onLinkClick }) {
//     const navigate = useNavigate();
//     const location = useLocation();
//     const currentPath = location.pathname.toLowerCase();
//     const { user } = useContext(AuthContext); // Get the user object from AuthContext

//     // Function to determine the active tab based on the current path
//     const getTab = () => {
//         const matchingTab = routeToTabMap[currentPath] ||
//             Object.keys(routeToTabMap).find(key => currentPath.startsWith(key))
//             ? routeToTabMap[Object.keys(routeToTabMap).find(key => currentPath.startsWith(key))]
//             : "Me"; // Default to "Me" if no match
//         return matchingTab;
//     };

//     const [activeTab, setActiveTab] = useState(getTab());

//     // Update activeTab whenever the URL changes
//     useEffect(() => {
//         const newActiveTab = getTab();
//         setActiveTab(newActiveTab);
//     }, [currentPath]); // Re-run when currentPath changes

//     const handleTabClick = (tab) => {
//         setActiveTab(tab);
//         navigate(`/${tab.toLowerCase().replace(" ", "")}`);
//         if (onLinkClick) { // Call the provided callback to close the mobile menu
//             onLinkClick();
//         }
//     };

//     // Filter tabs based on user role
//     const filteredTabs = allTabs.filter(tab => {
//         if (user && user.role === 'superadmin') {
//             // If the user is a superadmin, ONLY show the "Super Admin" tab
//             return tab === "Super Admin";
//         } else {
//             // If the user is NOT a superadmin (e.g., admin, employee, or not logged in),
//             // show all tabs EXCEPT "Super Admin"
//             return tab !== "Super Admin";
//         }
//     });
//     return (
//         // Conditional styling based on isMobile prop
//         <div className={`flex ${isMobile ? 'flex-col space-y-2 w-full' : 'items-center space-x-4'}`}>
//             {filteredTabs.map((tab) => ( // Use filteredTabs here
//                 <button
//                     key={tab}
//                     onClick={() => handleTabClick(tab)}
//                     className={`
//                         transition duration-200 ease-in-out
//                         ${isMobile
//                             ? `w-full text-left py-2 px-4 rounded-md text-base
//                                 ${activeTab === tab
//                                     ? "bg-blue-50 text-blue-700 font-semibold border-l-4 border-blue-600"
//                                     : "text-gray-700 hover:bg-gray-100"
//                                 }`
//                             : `text-sm px-3 py-1 rounded
//                                 ${activeTab === tab
//                                     ? "bg-blue-100 text-blue-700 border-b-[3px] border-blue-500"
//                                     : "text-gray-600 hover:text-gray-900"
//                                 }`
//                         }
//                     `}
//                 >
//                     {tab}
//                 </button>
//             ))}
//         </div>
//     );
// }
